import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { PrismIcon, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { sortBy } from '@nullvoxpopuli/ember-composable-helpers';
import { formatNumber, t } from 'ember-intl';
import { IncrementFor } from 'frontend-utils';
import MarketCustomReportingThumbnail from 'lumen/components/market/custom-reporting/thumbnail';
import type MarketCustomReportingTemplatesController from 'lumen/controllers/market/custom-reporting/templates';

interface MarketCustomReportingTemplatesSignature {
  Args: {
    controller: MarketCustomReportingTemplatesController;
    model: unknown;
  };
}

<template>
  <div
    class="custom-reporting__page flex w-full flex-col gap-400 pt-500 pr-800 pb-500 pl-800"
  >
    {{#if @controller.isLoading}}
      <div class="loading-bar-container">
        <PrismLoadingBar />
      </div>
    {{/if}}
    <div>
      {{#if @controller.access.hasCustomReportingEdit}}
        <LinkTo
          @route="market.custom-reporting.index"
          class="prism-link prism-link--secondary prism-link--standalone prism-link--small"
          data-test-id="custom-reporting-back-to-home"
        >
          <PrismIcon @color="inherit" @name="arrow-left" @size="small" />
          {{t "custom_reporting.home"}}
        </LinkTo>
      {{/if}}
      <div class="mt-200 flex justify-between">
        <h2 data-test-id="custom-reporting-templates-header">
          {{t "custom_reporting.made_for_you"}}
        </h2>
        <a
          class="prism-link prism-link--standalone prism-link--medium"
          data-test-id="custom-reporting-learn-more-link"
          href="https://help.mylighthouse.com/en/collections/9406616-custom-reporting-in-bi"
          rel="noopener noreferrer"
          target="_blank"
        >
          {{t "general.learn_more"}}
          <PrismIcon @color="inherit" @name="link-out" @size="small" />
        </a>
      </div>
    </div>
    <div class="grid grid-cols-3 gap-400">
      {{#if @controller.isLoading}}
        <IncrementFor @times={{5}}>
          <MarketCustomReportingThumbnail @isLoading={{true}} />
        </IncrementFor>
      {{else}}
        {{#each (sortBy "title:asc" @controller.dashboards) as |dashboard|}}
          <LinkTo
            @model={{dashboard}}
            @route="market.custom-reporting.dashboard"
          >
            <MarketCustomReportingThumbnail
              @imageAlt={{dashboard.title}}
              @imageURL={{dashboard.thumbnailUrl}}
              @title={{dashboard.title}}
              data-test-id="custom-reporting-thumbnail"
            >
              <:subtitle>
                <div class="flex flex-row items-center gap-100">
                  <PrismIcon @color="inherit" @name="visibility-on" />
                  {{formatNumber dashboard.viewCount}}
                </div>
              </:subtitle>
            </MarketCustomReportingThumbnail>
          </LinkTo>
        {{/each}}
      {{/if}}
    </div>
  </div>
</template> satisfies TOC<MarketCustomReportingTemplatesSignature>;
